/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { AccordionGroup } from '@coursera/cds-core';
import type { ButtonName } from '@coursera/event-pulse-types';

import type { ComputedItemType } from 'bundles/assess-common/lib/ComputedItem';
import type { QueryStateSuccessFragment } from 'bundles/assignment-submission/components/api/assignment-submission-api/__generated__/AssignmentSubmission';
import type { TrackingData } from 'bundles/assignment-submission/types/TrackingData';
import { type Track } from 'bundles/naptimejs/resources/programCurriculums.v1';
import SkillProgressDetailedListItem from 'bundles/skills-common/shared/verified-skills/SkillProgressDetailedListItem';
import type { VerifiedSkillsEnterpriseBadgeCollectionFragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseBadgeCollectionsQuery';
import type { VerifiedSkillsEnterpriseProgramsV1Fragment } from 'bundles/skills-common/shared/verified-skills/data/pathway-badge/__generated__/VerifiedSkillsEnterpriseProgramForCourseQuery';
import type { SkillAttainmentSkillAreaAttainmentFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import type { SkillPathwayCurriculumProductsFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillPathwayCurriculumProductsQuery';
import useGetSkillAreasWithUserData from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/useGetSkillAreasWithUserData';

type Props = {
  skillAreaAttainments: SkillAttainmentSkillAreaAttainmentFragment[];
  focusedSkillAreaId?: string;

  // props to handle retrying assessment when entering from and assignment results page
  item?: ComputedItemType;
  submissionState?: QueryStateSuccessFragment;
  itemMetadata?: ItemMetadata;
  refetchAssignmentSubmissionData?: () => Promise<void>;
  trackingData?: TrackingData;

  // render prop to avoid circular dependency
  renderRetryActionButton?: (
    submissionState: QueryStateSuccessFragment,
    itemMetadata: ItemMetadata,
    refetchAssignmentSubmissionData: () => Promise<void>,
    trackingData?: TrackingData,
    trackingButtonName?: ButtonName
  ) => React.ReactNode;

  // enrollment props for enrol cards
  enrollmentProps?: {
    programId: string;
    pathwayCollectionId?: string;
    programSlug?: string;
    thirdPartyOrganizationSlug?: string;
  };
  skillPathCurriculumProducts?: (SkillPathwayCurriculumProductsFragment | null)[];
  program?: VerifiedSkillsEnterpriseProgramsV1Fragment | null;
  enrolledBadgeCollection?: VerifiedSkillsEnterpriseBadgeCollectionFragment | null;
  skillPath?: Track;
  handleDialogOnClose?: () => void;
};

const styles = {
  accordionGroup: css`
    .cds-AccordionGroup-itemSpacing {
      margin-top: 0 !important;
    }
  `,
};

const SkillProgressDetailedList = ({
  skillAreaAttainments,
  focusedSkillAreaId,
  item,
  submissionState,
  itemMetadata,
  refetchAssignmentSubmissionData,
  trackingData,
  renderRetryActionButton,
  enrollmentProps,
  skillPathCurriculumProducts,
  program,
  enrolledBadgeCollection,
  skillPath,
  handleDialogOnClose,
}: Props) => {
  const { loading: skillAreasWithUserDataLoading, skillAreasWithUserData } = useGetSkillAreasWithUserData({
    programId: enrollmentProps?.programId || program?.id || '',
  });

  if (skillAreaAttainments?.length === 0) {
    return null;
  }

  const getSkillAreaAttainmentUserData = (skillAreaId: string) => {
    return skillAreasWithUserData.find((skillArea) => skillArea.skillArea.id === skillAreaId);
  };

  return (
    <AccordionGroup spacing={8} css={styles.accordionGroup}>
      {skillAreaAttainments.map((skillAreaAttainment) => (
        <SkillProgressDetailedListItem
          key={skillAreaAttainment.skillArea.id}
          skillAreaAttainment={skillAreaAttainment}
          focusedSkillAreaId={focusedSkillAreaId}
          skillPathCurriculumProducts={skillPathCurriculumProducts}
          program={program}
          enrolledBadgeCollection={enrolledBadgeCollection}
          skillPath={skillPath}
          item={item}
          submissionState={submissionState}
          itemMetadata={itemMetadata}
          refetchAssignmentSubmissionData={refetchAssignmentSubmissionData}
          trackingData={trackingData}
          renderRetryActionButton={renderRetryActionButton}
          enrollmentProps={enrollmentProps}
          skillAreaWithUserData={getSkillAreaAttainmentUserData(skillAreaAttainment.skillArea.id)}
          skillAreasWithUserDataLoading={skillAreasWithUserDataLoading}
          handleDialogOnClose={handleDialogOnClose}
        />
      ))}
    </AccordionGroup>
  );
};

export default SkillProgressDetailedList;
